import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OptionModel } from "../../common/types/OptionModel";
import { Form, Formik, FormikProps } from "formik";
import FormObserver from "../components/FormElements/FormObserver";
import { FormInput, FormSelector } from "../components/FormElements";
import FormMultiSelect from "../components/FormElements/FormMultiSelect";
import { customerInsertUpdateSchema } from "../../common/validators/customerInsertUpdateSchema";
import commonNetwork from "../../common/api/commonNetwork";
import { toast } from "react-toastify";
import Button from "../components/Elements/Button";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { getAllCities, getAllDistricts } from "../../store/slices/addressSlice";
import useGetUserName from "../../hooks/useGetUserName";
import { getNavigationUrl } from "../../common/helpers/navigationHelpers";
import { ApiResponseType } from "../../common/api/apiHelper";
import Loading from "../components/Loading";
import { successMessages } from "../../constants/messages/successMessages";
import PageTitle from "../components/PageTitle";
import { pageTitleTexts } from "../../constants/texts/pageTitleTexts";
import { buttonTexts } from "../../constants/texts/buttonTexts";
import { refreshCustomerTable } from "../../common/helpers/refreshTables/refreshCustomerTable";
import FormMaskedInput from "../components/FormElements/FormMaskedInput";
import { useRecoilValue } from "recoil";
import { tenantState } from "../../store/recoil/tenant/atoms";
import { getAllPaymentSet } from "../../store/slices/paymentSetSlice";
import { usePermissionCheck } from "../../hooks/usePermissionCheck";
import { UserOperationTypes } from "../../common/types/AuthorizationProps";

type CustomerDetailProp = {
    customerCode: string,
    customerName: string,
    mobile: string,
    phone: string,
    email: string,
    countryId: any,
    cityId: OptionModel | any,
    districtId: OptionModel | any,
    postalCode: string,
    address: string,
    taxOffice: string,
    taxCode: string,
    tcCode: string,
    customerUserList: any[],
    status: boolean,
    paymentSetId: number
}

const InsertUpdateCustomer = () => {
    usePermissionCheck(UserOperationTypes["Müşteri Listesi"], "Müşteri İşlemleri izni yok");
    const countryData: OptionModel[] = [
        { value: 1, label: "Türkiye" }
    ];

    const { firmInformations } = useRecoilValue(tenantState);
    const { licenseKey, taxCode } = firmInformations;
    const formRef = useRef<FormikProps<CustomerDetailProp>>(null);
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();


    let { customerId }: any = useParams();
    const loadActive = customerId > 0;

    const [formValues, setFormValues] = useState<CustomerDetailProp>({
        customerCode: "",
        customerName: "",
        mobile: "",
        phone: "",
        email: "",
        countryId: "",
        cityId: null,
        districtId: null,
        postalCode: "",
        address: "",
        taxOffice: "",
        taxCode: "",
        tcCode: "",
        customerUserList: [],
        status: false,
        paymentSetId: 0
    });

    const [postLoading, setPostLoading] = useState(false);
    const [getLoading, setGetLoading] = useState(loadActive);
    const [cityOptions, setCityOptions] = useState<OptionModel[]>([]);
    const [filteredDistrictOptions, setFilteredDistrictOptions] = useState<OptionModel[]>([]);
    const [userOptions, setUserOptions] = useState<OptionModel[]>([]);
    const [paymentSetOptions, setPaymentSetOptions] = useState<OptionModel[]>([]);
    const [userLoading, setUserLoading] = useState<boolean>(false);

    const backToTheCustomerUrl = getNavigationUrl("/musteri-listesi");
    const navigate = useNavigate();
    const { firmname } = useParams();

    useGetUserName(setUserOptions, setUserLoading);

    useEffect(() => {
        fetchCities();
    }, [dispatch]);

    useEffect(() => {
        if (formValues.cityId) {
            fetchDistricts();
        }
    }, [formValues.cityId]);

    useEffect(() => {
        if (customerId > 0) {
            selectCustomerInformation();
        }
    }, [customerId]);

    useEffect(() => {
        getPaymentSet();
    }, []);


    const fetchCities = async () => {
        try {
            const resultActions = await dispatch(getAllCities())
            if (getAllCities.fulfilled.match(resultActions)) {
                const citiesData = resultActions.payload;
                const options: OptionModel[] = citiesData.map((item: any) => ({
                    value: item.cityid,
                    label: item.city,
                }));
                setCityOptions(options);
            } else {
            }
        } catch (error) {
        }
    }

    const fetchDistricts = async () => {
        const selectedCity = formValues.cityId;
        // Ensure selectedCity is not null
        if (!selectedCity) {
            setFilteredDistrictOptions([]);
            return;
        }
        try {
            const resultAction = await dispatch(getAllDistricts());
            if (getAllDistricts.fulfilled.match(resultAction)) {
                const districtsData = resultAction.payload;
                const filteredDistricts = districtsData.filter((district: any) =>
                    selectedCity == district.cityid
                );
                setFilteredDistrictOptions(filteredDistricts.map((district: any) => ({
                    value: district.districtid,
                    label: district.districtname,
                })));
            } else {
            }
        } catch (error) {
        }
    }

    const selectCustomerInformation = async () => {
        let result: ApiResponseType;
        let customerData: CustomerDetailProp;
        result = await commonNetwork.get("/customer/" + customerId);

        if (result.isSuccess) {
            const data = result.data;

            customerData = {
                customerCode: data.customerCode != null ? data.customerCode : "",
                customerName: data.customerName != null ? data.customerName : "",
                mobile: data.mobile ? formatPhoneNumber(data.mobile) : "",
                phone: data.phone ? formatPhoneNumber(data.phone) : "",
                email: data.email != null ? data.email : "",
                countryId: data.countryId,
                cityId: data.cityId,
                districtId: data.districtId,
                postalCode: data.postalCode != null ? data.postalCode : "",
                address: data.address != null ? data.address : "",
                taxOffice: data.taxOffice != null ? data.taxOffice : "",
                taxCode: data.taxCode != null ? data.taxCode : "",
                tcCode: data.tcCode != null ? data.tcCode : "",
                customerUserList: data.customerUserList.map((user: any) => ({
                    value: user.id,
                    label: user.label,
                })),
                status: data.status,
                paymentSetId: data.paymentSetId || 0
            };
            setFormValues(customerData);
        } else {
            if (result.errorMessage == null) {
                toast.error("Kullanıcı bilgileri alınamadı. Lütfen tekrar deneyin.");
            } else {
                toast.error(result.errorMessage);
            }
            navigate("/" + firmname + "/kullanici-listesi");
        }
        setGetLoading(false);
    };

    const formatPhoneNumber = (phoneNumber: string) => {
        let cleaned = phoneNumber.replace(/\D/g, "");

        if (cleaned.startsWith("0")) {
            cleaned = cleaned.substring(1);
        } else if (cleaned.startsWith("90") && cleaned.length > 10) {
            cleaned = cleaned.substring(2);
        }

        if (cleaned.length === 10) {
            return cleaned.substring(0, 3) + " " +
                cleaned.substring(3, 6) + " " +
                cleaned.substring(6, 8) + " " +
                cleaned.substring(8);
        }

        return cleaned;
    }

    const onSubmit = async () => {

        const transformedCustomerUserList = formValues.customerUserList.map(user => ({
            value: user.value,
            label: user.label,
            id: user.value,
        }));

        const parameters: any = {
            customerCode: formValues.customerCode,
            customerName: formValues.customerName,
            taxCode: formValues.taxCode,
            tcCode: formValues.tcCode,
            taxOffice: formValues.taxOffice,
            countryId: formValues.countryId,
            cityId: formValues.cityId,
            districtId: formValues.districtId,
            postalCode: formValues.postalCode,
            address: formValues.address,
            phone: formValues.phone
                ?.replace("(", "")
                .replace(")", "")
                .replace(/\s+/g, ""),
            mobile: formValues.mobile
                ?.replace("(", "")
                .replace(")", "")
                .replace(/\s+/g, ""),
            email: formValues.email,
            customerUserList: transformedCustomerUserList,
            paymentSetId: formValues.paymentSetId
        }

        if (customerId > 1) {
            parameters.id = parseInt(customerId);
        }

        setPostLoading(true);
        let result: ApiResponseType;

        try {
            if (customerId > 0) {
                result = await commonNetwork.post(`/customer/update-customer`, parameters)

            } else {
                result = await commonNetwork.post(`/customer/`, parameters)
            }

            if (result.isSuccess) {
                refreshCustomerTable(dispatch);
                toast.success(customerId > 0 ? successMessages.update : successMessages.add);
                navigate(getNavigationUrl("/musteri-listesi"));
            } else {
                toast.error(result.errorMessage);
            }
        } catch (error) {
            toast.error("Hata: " + error + "\nİşlemi tekrar deneyin ya da çözüm için desteğe ulaşın.");
        } finally {
            setPostLoading(false);
        }
    }

    const onUpdate = (value: CustomerDetailProp) => {
        setFormValues({ ...formValues, ...value });
    }

    function goToBack() {
        navigate(backToTheCustomerUrl);
    }

    const getPaymentSet = async () => {
        const apiParams = { licenseKey: licenseKey, taxCode: taxCode };
        const params: any = {
            parameters: {},
            apiParams: apiParams
        };
        const resultActions = await dispatch(getAllPaymentSet(params));
        if (getAllPaymentSet.fulfilled.match(resultActions)) {
            const paymentSetData = resultActions.payload;
            let options: OptionModel[] = [
                {
                    value: 0,
                    label: "Ödeme Kuralı Uygulanmasın"
                }
            ];
            const newOptions = paymentSetData
            .filter((item: any) => item.status === true)
            .map((item: any) => ({
                value: item.paymentSetId,
                label: item.setDesc
            }));

            options = [...options, ...newOptions];
            setPaymentSetOptions(options);
        }
    };

    return (
        getLoading ? (
            <Loading desc={customerId > 0 ? "Müşteri bilgileri yükleniyor..." : "Müşteri kayıt ekranı yükleniyor..."} />
        ) : (
            <div className="pb-7">
                <PageTitle title={customerId > 0 ? pageTitleTexts.updateCustomer : pageTitleTexts.insertCustomer} />
                <Formik
                    innerRef={formRef}
                    initialValues={formValues}
                    validationSchema={customerInsertUpdateSchema}
                    enableReinitialize
                    onSubmit={onSubmit}
                >
                    {({ }) => (
                        <Form>
                            <FormObserver
                                watch={(val: any) => {
                                    onUpdate(val);
                                }}
                            />
                            <div className="w-full">
                                <div className="px-4 py-2 flex flex-col justify-start space-y-2">
                                    <div className="flex flex-row items-center">
                                        <img src={"/icons/add-user.svg"} className="h-6 w-6 black" />
                                        <span className="text-lg font-bold leading-7 text-contrast-90 sm:truncate ml-2">
                                            {customerId > 0 ? "Müşteri Kartını Düzenle" : "Yeni Müşteri Kartı"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="mx-4 px-4 py-2 bg-white shadow-md mb-4 pb-4 mt-1 rounded-md">
                                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                                    <div className="mt-3">
                                        <h1 className="font-bold text-contrast-90"> Müşteri Temel Bilgileri</h1>
                                        <span className="text-contrast-50 text-md">
                                            Müşterinizin temel bilgilerini girerek müşteri kaydınızı oluşturun.
                                        </span>
                                    </div>
                                    <div className="w-full col-span-2">
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                <FormInput
                                                    label="Müşteri Adı*"
                                                    name="customerName"
                                                    type="text"
                                                    maxLength={151}
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <FormInput
                                                    label="Müşteri Kodu*"
                                                    name="customerCode"
                                                    type="text"
                                                    maxLength={51}
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                <FormInput
                                                    label="Vergi Dairesi"
                                                    name="taxOffice"
                                                    type="text"
                                                    maxLength={101}
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <FormInput
                                                    label="Vergi Numarası"
                                                    name="taxCode"
                                                    type="text"
                                                    mask="9999999999"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                <FormInput
                                                    label="TC Kimlik Numarası"
                                                    name="tcCode"
                                                    type="text"
                                                    mask="99999999999"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <hr />
                                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                                    <div className="mt-3">
                                        <h1 className="font-bold text-contrast-90">İletişim Bilgileri</h1>
                                        <span className="text-contrast-50 text-md">
                                            Müşterinize ödeme linki gönderebilmek ve bilgilendirme mesajları paylaşabilmek için e-posta adresi ve cep telefonu bilgilerini giriniz.
                                        </span>
                                    </div>
                                    <div className="w-full col-span-2">
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                <FormInput
                                                    label="E-Posta Adresi*"
                                                    name="email"
                                                    type="text"
                                                    maxLength={101}
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                {/* <FormInput
                                                    label="GSM*"
                                                    name="phone"
                                                    type="text"
                                                    placeholder="5xx xxx xx xx"
                                                    mask={formValues.phone ? (formValues.phone.startsWith("0") ? "9(999) 999 99 99" : "(999) 999 99 99") : "(999) 999 99 99"}
                                                /> */}
                                                <FormMaskedInput
                                                    label="GSM*"
                                                    name="phone"
                                                    maskOptions={{
                                                        delimiter: ' ',
                                                        blocks: [3, 3, 2, 2],
                                                        numericOnly: true
                                                    }}
                                                    placeholder="5xx xxx xx xx"
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <FormInput
                                                    label="Telefon"
                                                    name="mobile"
                                                    type="text"
                                                    mask={formValues.mobile ? (formValues.mobile.startsWith("0") ? "9(999) 999 99 99" : "(999) 999 99 99") : "(999) 999 99 99"}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr />

                                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                                    <div className="mt-3">
                                        <h1 className="font-bold text-contrast-90">Adres Bilgileri</h1>
                                        <span className="text-contrast-50 text-md">
                                            Müşteriniz adres bilgilerini giriniz.
                                        </span>
                                    </div>
                                    <div className="w-full col-span-2">
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                <FormSelector
                                                    options={countryData}
                                                    label="Ülke"
                                                    placeholder={"Ülke seçiniz..."}
                                                    value={formValues.countryId ? countryData.find((item) => item.value == formValues.countryId) : countryData[0]}
                                                    name="countryId"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                <FormSelector
                                                    options={cityOptions}
                                                    label="Şehir"
                                                    placeholder={"Şehir seçiniz..."}
                                                    value={cityOptions.find((item) => item.value == formValues.cityId)}
                                                    name="cityId"
                                                    isClearable
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <FormSelector
                                                    options={filteredDistrictOptions}
                                                    label="İlçe"
                                                    placeholder={"İlçe seçiniz..."}
                                                    value={filteredDistrictOptions.find((item) => item.value == formValues.districtId)}
                                                    name="districtId"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                <FormInput
                                                    label="Adres"
                                                    name="address"
                                                    type="text"
                                                    maxLength={201}
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <FormInput
                                                    label="Posta Kodu"
                                                    name="postalCode"
                                                    type="text"
                                                    maxLength={21}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr />

                                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                                    <div className="mt-3">
                                        <h1 className="font-bold text-contrast-90">Müşteri - Kullanıcı Yetkilendirme</h1>
                                        <span className="text-contrast-50 text-md">
                                            Müşterinizden ödeme alma yetkisi olan kullanıcıları seçiniz.
                                        </span>
                                    </div>
                                    <div className="w-full col-span-2">
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                <FormMultiSelect
                                                    options={userOptions}
                                                    name="customerUserList"
                                                    placeholder="Kullanıcı Seçiniz..."
                                                    value={userOptions.filter((item: any) => formValues.customerUserList.includes(item))}
                                                    label="Bağlı Olduğu Kullanıcılar"
                                                    loading={userLoading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr />

                                <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                                    <div className="mt-3">
                                        <h1 className="font-bold text-contrast-90">Ödeme Kuralı</h1>
                                        <span className="text-contrast-50 text-md">
                                            Müşterinize özel bir ödeme kuralı tanımınız var ise seçiniz.
                                        </span>
                                    </div>
                                    <div className="w-full col-span-2">
                                        <div className="grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 xl:grid-cols-2">
                                            <div className="mt-3">
                                                <FormSelector
                                                    options={paymentSetOptions}
                                                    label="Ödeme Kuralı Seçiniz"
                                                    value={formValues.paymentSetId ? paymentSetOptions.find((item) => item.value == formValues.paymentSetId) : paymentSetOptions[0]}
                                                    name="paymentSetId"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr />

                                <div className="mt-3 justify-end mr-3 flex flex-row">
                                    <Button
                                        type="button"
                                        color="gray"
                                        hover="primary"
                                        size="xs"
                                        callback={goToBack}
                                        className="mr-2"
                                    >
                                        {buttonTexts.goToBack}
                                    </Button>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        hover="primary"
                                        size="xs"
                                        disabled={postLoading}
                                        loading={postLoading}
                                    >
                                        {customerId > 0 ? buttonTexts.update : buttonTexts.save}
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        )
    );
}

export default InsertUpdateCustomer;
